/*
	Черновики «нового чата» без session_id (формат legacy ba-drafts)
*/

export const DRAFTS_STORAGE_KEY = "ba-drafts";
export const DRAFTS_CHANGED_EVENT = "ba-drafts-changed";

export type DraftSession = {
	draft_id: string;
	title: string;
	message_text: string;
	updated_at: string;
	is_custom_title: boolean;
};

const safeNowIso = (): string => new Date().toISOString();

const dispatchDraftsChanged = () => {
	window.dispatchEvent(new CustomEvent(DRAFTS_CHANGED_EVENT));
};

const isDraftSession = (value: unknown): value is DraftSession => {
	if (!value || typeof value !== "object") return false;
	const record = value as Record<string, unknown>;
	return (
		typeof record.draft_id === "string" &&
		typeof record.title === "string" &&
		typeof record.message_text === "string" &&
		typeof record.updated_at === "string" &&
		typeof record.is_custom_title === "boolean"
	);
};

export const createDraftId = (): string => {
	const randomPart = Math.random().toString(36).slice(2, 8);
	return `draft-${Date.now()}-${randomPart}`;
};

export const formatDraftTitle = (dateValue: Date | string = new Date()): string => {
	const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");
	const hh = String(date.getHours()).padStart(2, "0");
	const min = String(date.getMinutes()).padStart(2, "0");
	return `Новий чат ${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

export const buildDraftTitleFromText = (
	text: string,
	maxLen = 80,
): string => {
	const oneLine = String(text || "")
		.replace(/\s+/g, " ")
		.trim();
	if (!oneLine) return "";
	if (oneLine.length <= maxLen) return oneLine;
	return `${oneLine.slice(0, maxLen - 1).trimEnd()}…`;
};

export const loadDraftSessions = (): DraftSession[] => {
	try {
		const raw = localStorage.getItem(DRAFTS_STORAGE_KEY);
		if (!raw) return [];
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isDraftSession).map((item) => ({
			draft_id: item.draft_id,
			title:
				typeof item.title === "string" && item.title.trim()
					? item.title
					: formatDraftTitle(item.updated_at || safeNowIso()),
			message_text:
				typeof item.message_text === "string" ? item.message_text : "",
			updated_at: item.updated_at || safeNowIso(),
			is_custom_title: item.is_custom_title === true,
		}));
	} catch {
		return [];
	}
};

export const saveDraftSessions = (drafts: DraftSession[]): void => {
	try {
		localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
		dispatchDraftsChanged();
	} catch {
		/* ignore quota errors */
	}
};

export const getDraftById = (draftId: string): DraftSession | null =>
	loadDraftSessions().find((d) => d.draft_id === draftId) ?? null;

export const getVisibleDraftSessions = (): DraftSession[] =>
	loadDraftSessions().filter((d) => d.message_text.trim().length > 0);

export const getDraftMessageText = (draftId: string | null): string => {
	if (!draftId) return "";
	return getDraftById(draftId)?.message_text ?? "";
};

export const upsertDraft = (
	draftId: string,
	messageText: string,
): DraftSession | null => {
	const normalizedText = String(messageText ?? "");
	const nowIso = safeNowIso();
	const drafts = loadDraftSessions();
	const existingIndex = drafts.findIndex((d) => d.draft_id === draftId);

	if (!normalizedText.trim()) {
		if (existingIndex >= 0) {
			drafts.splice(existingIndex, 1);
			saveDraftSessions(drafts);
		}
		return null;
	}

	if (existingIndex >= 0) {
		const existing = drafts[existingIndex];
		const updated: DraftSession = {
			...existing,
			message_text: normalizedText,
			updated_at: nowIso,
		};
		if (!existing.is_custom_title) {
			const autoTitle = buildDraftTitleFromText(normalizedText);
			updated.title = autoTitle || formatDraftTitle(nowIso);
		} else if (!existing.title.trim()) {
			updated.title = formatDraftTitle(nowIso);
		}
		drafts[existingIndex] = updated;
		saveDraftSessions(drafts);
		return updated;
	}

	const autoTitle = buildDraftTitleFromText(normalizedText);
	const created: DraftSession = {
		draft_id: draftId,
		title: autoTitle || formatDraftTitle(nowIso),
		message_text: normalizedText,
		updated_at: nowIso,
		is_custom_title: false,
	};
	drafts.unshift(created);
	saveDraftSessions(drafts);
	return created;
};

export const removeDraft = (draftId: string): void => {
	const drafts = loadDraftSessions().filter((d) => d.draft_id !== draftId);
	saveDraftSessions(drafts);
};

export const setDraftCustomTitle = (draftId: string, title: string): void => {
	const drafts = loadDraftSessions();
	const index = drafts.findIndex((d) => d.draft_id === draftId);
	if (index < 0) return;
	const trimmed = title.trim();
	drafts[index] = {
		...drafts[index],
		title: trimmed || formatDraftTitle(drafts[index].updated_at),
		is_custom_title: true,
		updated_at: safeNowIso(),
	};
	saveDraftSessions(drafts);
};

export const persistDraftText = (
	draftId: string | null,
	text: string,
): string | null => {
	if (!draftId) return null;
	const hasText = text.trim().length > 0;
	if (!hasText) {
		removeDraft(draftId);
		return null;
	}
	upsertDraft(draftId, text);
	return draftId;
};
